import { LOCALES_RICH } from "@core-types/locales.enum";
import { SeoHelper } from "@helpers/seo.helper";
import useAppLanguage from "@hooks/language/useAppLanguage";
import useTranslationCondition from "@hooks/useTranslationCondition";
import React, { useCallback } from "react";
import { useLocation } from "react-router";
import ogEn from "/og_picture_en.webp";
import ogFr from "/og_picture_fr.webp";

const envVars = import.meta.env;

interface ISEOProps {
  children?: React.ReactNode;
  description: string;
  title: string;
}

const SEO: React.FC<ISEOProps> = ({ description, title, children = <></> }) => {
  const location = useLocation();
  const { lang } = useAppLanguage();
  const ogLocale = useTranslationCondition({
    english: LOCALES_RICH.en_US,
    french: LOCALES_RICH.fr_FR,
  });

  const setTitle = () => {
    document.title = title;
    SeoHelper.setOgMetaData("title", title);
    SeoHelper.setTwitterMetaData("title", title);
  };

  const setDescription = () => {
    SeoHelper.setMetaData("name", "description", description);
    SeoHelper.setOgMetaData("description", description);
    SeoHelper.setTwitterMetaData("description", description);
  };

  const setImage = () => {
    if (ogLocale === LOCALES_RICH.fr_FR) {
      SeoHelper.setOgMetaData("image", `${envVars.VITE_FRONT_URL}${ogFr}`);
      SeoHelper.setTwitterMetaData("image", `${envVars.VITE_FRONT_URL}${ogFr}`);
      return;
    }
    SeoHelper.setOgMetaData("image", `${envVars.VITE_FRONT_URL}${ogEn}`);
    SeoHelper.setTwitterMetaData("image", `${envVars.VITE_FRONT_URL}${ogEn}`);
  };

  const setUrl = () => {
    const url = `${envVars.VITE_FRONT_URL}${location.pathname}`;
    SeoHelper.setOgMetaData("url", url);
    SeoHelper.setTwitterMetaData("url", url);
    SeoHelper.setCanonical(url);
  };

  const setMetaLanguage = useCallback(() => {
    const html = document.querySelector("html");
    if (html) {
      html.setAttribute("lang", lang.currentLanguage);
    }

    // Doc: https://ogp.me/#optional
    const ogAlternateLocale = [
      ogLocale === LOCALES_RICH.en_US ? LOCALES_RICH.fr_FR : LOCALES_RICH.en_US,
    ];
    SeoHelper.setOgMetaData("locale", ogLocale);
    ogAlternateLocale.map((alternate) => {
      SeoHelper.setOgMetaData("locale:alternate", alternate);
    });
  }, [lang.currentLanguage, ogLocale]);

  const updateMetaTags = () => {
    setTitle();
    setDescription();
    setMetaLanguage();
    setImage();
    setUrl();
  };

  React.useEffect(() => {
    updateMetaTags();
  }, [title, description, location.pathname, ogLocale]);

  return <>{children}</>;
};

export default SEO;
